import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { enrollmentService } from '../../services/enrollmentService';
import { SKILL_TREES } from '../../data/skillTrees';
import { buildTimeline, daysBetween } from '../../utils/timeline';
import { TimelineCard } from './TimelineCard';
import type { Enrollment } from '../../types';

export const StudentTimeline: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const { userId: paramUserId } = useParams<{ userId?: string }>();
  const targetUserId = paramUserId || user?.uid;
  const { qualifications, allUsers } = useData(targetUserId);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);

  // Load progress for the target user
  const { getProgress } = useData(targetUserId);
  const progress = targetUserId ? getProgress(targetUserId) : {};

  const targetUser = useMemo(
    () => allUsers.find((u) => u.uid === targetUserId),
    [allUsers, targetUserId],
  );

  useEffect(() => {
    if (!targetUserId) return;
    const load = async () => {
      try {
        const enrs = await enrollmentService.getByUser(targetUserId);
        setEnrollments(enrs);
      } catch (err) {
        console.error('Failed to load enrollments:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [targetUserId]);

  const isOwnTimeline = !paramUserId || paramUserId === user?.uid;
  const displayName = isOwnTimeline ? 'My' : (targetUser?.displayName || targetUser?.email || 'Student');

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>Timeline</h1>
        <p style={{ color: t.textSecondary }}>Loading...</p>
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>
          {displayName} Timeline
        </h1>
        <div
          className="rounded-lg border p-8 text-center"
          style={{ background: t.cardBg, borderColor: t.border }}
        >
          <p style={{ color: t.textSecondary }}>
            {isOwnTimeline
              ? "You haven't been enrolled in a skill tree yet. Contact your administrator."
              : 'This student has no enrollments. Enroll them from the Enrollments page.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: t.textPrimary }}>
          {displayName} Timeline
        </h1>
        <p className="mt-2" style={{ color: t.textSecondary }}>
          {isOwnTimeline
            ? 'Track your deadlines and pace through each qualification'
            : `Viewing timeline for ${targetUser?.displayName || targetUser?.email || 'student'}`}
        </p>
      </div>

      {enrollments.map((enrollment) => {
        const tree = SKILL_TREES.find((tr) => tr.id === enrollment.skillTreeId);
        if (!tree) return null;

        const timeline = buildTimeline(enrollment, qualifications, tree.qualificationIds, progress || {});
        const daysSinceStart = daysBetween(enrollment.startDate, new Date());

        return (
          <div key={enrollment.id} className="space-y-4">
            {/* Tree Header */}
            <div
              className="rounded-lg border p-4"
              style={{ background: t.cardBg, borderColor: t.border }}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-xl font-semibold" style={{ color: t.textPrimary }}>
                    {tree.name}
                  </h2>
                  <p className="text-sm mt-1" style={{ color: t.textSecondary }}>
                    Started{' '}
                    {enrollment.startDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    {daysSinceStart > 0 && (
                      <span className="ml-2">
                        ({daysSinceStart} day{daysSinceStart !== 1 ? 's' : ''} ago)
                      </span>
                    )}
                  </p>
                </div>

                {/* Overall summary */}
                <div className="flex gap-4">
                  <Stat
                    label="Complete"
                    value={`${timeline.filter((e) => e.status === 'complete').length}/${timeline.length}`}
                    color={t.success}
                  />
                  <Stat
                    label="Overdue"
                    value={String(timeline.filter((e) => e.status === 'overdue').length)}
                    color={timeline.some((e) => e.status === 'overdue') ? t.fail : t.textSecondary}
                  />
                </div>
              </div>
            </div>

            {/* Timeline Cards */}
            {timeline.map((entry) => (
              <TimelineCard key={entry.qualId} entry={entry} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => {
  const { t } = useTheme();
  return (
    <div className="text-center">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs uppercase tracking-wide" style={{ color: t.textSecondary }}>{label}</div>
    </div>
  );
};
